package com.evoapps.evoshift.domain.spreadsheet

/*
 * SH-214 (first written for SH-123's sales import): reading a spreadsheet the person picked,
 * dropped or pasted, on the device (nothing is uploaded), the same way on every platform:
 * - CSV / TSV / pasted cells: [CsvReader] (separator and encoding detected);
 * - .xlsx: [XlsxReader] over [ZipReader], whose DEFLATE entries the platform inflates
 *   ([inflateRaw]: java.util.zip on the JVM and Android, DecompressionStream in the browser, the
 *   Compression framework on iOS), with the guards against oversized and "zip bomb" files.
 * Rows are cell texts (see [Sheet]); what the columns mean is up to the caller (the staff import
 * of SH-214, the sales import of SH-123, the roster import of SH-217).
 */

/** The kind of file a table came from. */
enum class SpreadsheetFormat {
    /** Comma, semicolon or tab separated text (a .csv/.tsv/.txt file). */
    CSV,
    /** Cells copied from Excel or Google Sheets and pasted (tab separated). */
    PASTE,
    /** An Office Open XML workbook (.xlsx). */
    XLSX
}

/**
 * One sheet: its [name] (a CSV's is empty) and its rows of cell texts, as the spreadsheet shows
 * them, without the formatting: a missing cell is "", texts as typed, numbers as stored ("1234.5",
 * no grouping), booleans "TRUE"/"FALSE", the cached value of a formula, and cells with a date or
 * time format as "2026-02-01", "2026-02-01 08:30" or "08:30". Row i is the spreadsheet's row i + 1
 * (empty rows stay, as empty lists), so problems can name the row the person sees.
 */
data class Sheet(val name: String, val rows: List<List<String>>) {
    /** The number of columns: the widest row. */
    val width: Int get() = rows.maxOfOrNull { it.size } ?: 0

    /** Whether no cell holds anything. */
    val isBlank: Boolean get() = rows.none { row -> row.any { it.isNotBlank() } }
}

/** A file read: its [format] and sheets (a CSV has one); never empty. */
data class Spreadsheet(val format: SpreadsheetFormat, val sheets: List<Sheet>) {
    /** The sheet to start with: the first one with something in it. */
    val firstFilledSheet: Int get() = sheets.indexOfFirst { !it.isBlank }.coerceAtLeast(0)
}

/** Why a file cannot be read; the UI explains each one. */
enum class SpreadsheetProblem {
    /** Nothing in it (or only blank cells). */
    EMPTY,
    /** Larger than [SpreadsheetReader.MAX_FILE_BYTES], or more rows than the reader takes. */
    TOO_LARGE,
    /** Unpacks to more than the limits allow (a "zip bomb", or an absurdly large workbook). */
    TOO_LARGE_UNPACKED,
    /** The old binary Excel format (.xls): save it as .xlsx or CSV. */
    LEGACY_XLS,
    /** Encrypted with a password: save a copy without one. */
    PASSWORD_PROTECTED,
    /** A zip that is not an Excel workbook (an .ods, .numbers, .docx...). */
    NOT_A_SPREADSHEET,
    /** Damaged, cut short, or not a spreadsheet at all (an image, a PDF...). */
    CORRUPT
}

class SpreadsheetException(val problem: SpreadsheetProblem, detail: String? = null) :
    IllegalArgumentException("Spreadsheet: $problem${detail?.let { " ($it)" }.orEmpty()}")
